/**
 * Guided Plan Builder
 * Step-by-step wizard for SMEs to create business plans
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuidedPlanBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const templateId = searchParams.get('template');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [template, setTemplate] = useState(null);
  const [planData, setPlanData] = useState({
    name: '',
    company_name: '',
    industry: '',
    fiscal_year_start: 1,
    currency: 'ETB',
    description: ''
  });
  const [completeness, setCompleteness] = useState(0);

  const steps = [
    { id: 1, title: 'Basic Information', icon: '📋' },
    { id: 2, title: 'Executive Summary', icon: '📝' },
    { id: 3, title: 'Company Overview', icon: '🏢' },
    { id: 4, title: 'Market Analysis', icon: '📊' },
    { id: 5, title: 'Products/Services', icon: '📦' },
    { id: 6, title: 'Financial Projections', icon: '💰' },
    { id: 7, title: 'Funding Request', icon: '💵' },
    { id: 8, title: 'Risk Assessment', icon: '⚠️' },
    { id: 9, title: 'Review & Submit', icon: '✅' }
  ];

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/plan-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      setPlanData({ ...planData, industry: response.data.industry });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const savePlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/business-plans`, planData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Plan saved successfully!');
      return response.data;
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Basic Information</h2>
            <p className="text-gray-600">Let's start with the basics about your business</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Plan Name *</label>
                <input
                  type="text"
                  value={planData.name}
                  onChange={(e) => setPlanData({...planData, name: e.target.value})}
                  placeholder="e.g., 2026 Business Plan"
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Company Name *</label>
                <input
                  type="text"
                  value={planData.company_name}
                  onChange={(e) => setPlanData({...planData, company_name: e.target.value})}
                  placeholder="e.g., Acme Manufacturing PLC"
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Industry</label>
                <select
                  value={planData.industry}
                  onChange={(e) => setPlanData({...planData, industry: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="manufacturing">Manufacturing</option>
                  <option value="trading">Trading</option>
                  <option value="services">Services</option>
                  <option value="agriculture">Agriculture</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={planData.currency}
                  onChange={(e) => setPlanData({...planData, currency: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="ETB">Ethiopian Birr (ETB)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Brief Description</label>
              <textarea
                value={planData.description}
                onChange={(e) => setPlanData({...planData, description: e.target.value})}
                placeholder="Briefly describe your business..."
                className="w-full border rounded-lg px-4 py-2 h-24"
              />
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Executive Summary</h2>
            <p className="text-gray-600">Summarize your business in 2-3 paragraphs</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Include: What your business does, market opportunity, competitive advantage, financial highlights, and funding need.
              </p>
            </div>
            
            <textarea
              placeholder="Your executive summary (will be auto-generated later, or write manually)..."
              className="w-full border rounded-lg px-4 py-2 h-48"
            />
            
            <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700">
              🤖 Generate with AI
            </button>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Company Overview</h2>
            <p className="text-gray-600">Tell us about your company structure and management</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Legal Structure</label>
                <select className="w-full border rounded-lg px-4 py-2">
                  <option>Sole Proprietorship</option>
                  <option>Private Limited Company (PLC)</option>
                  <option>Share Company</option>
                  <option>Partnership</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Registration Number</label>
                <input type="text" placeholder="TIN or registration number" className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Year Established</label>
                <input type="number" placeholder="e.g., 2020" className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Number of Employees</label>
                <input type="number" placeholder="e.g., 15" className="w-full border rounded-lg px-4 py-2" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Management Team</label>
              <textarea
                placeholder="List key managers, their roles, and experience..."
                className="w-full border rounded-lg px-4 py-2 h-32"
              />
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Market Analysis</h2>
            <p className="text-gray-600">Define your target market and competition</p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                📊 This section will open the full Market Analysis form. Complete TAM/SAM/SOM and competitor analysis.
              </p>
            </div>
            
            <button 
              onClick={() => window.open('/bank/market-analysis/demo', '_blank')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
            >
              Open Market Analysis Form →
            </button>
          </div>
        );
      
      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Products & Services</h2>
            <p className="text-gray-600">Describe what you sell and how you deliver value</p>
            
            <div>
              <label className="block text-sm font-medium mb-2">Product/Service Description</label>
              <textarea
                placeholder="Describe your main products or services..."
                className="w-full border rounded-lg px-4 py-2 h-32"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Pricing Strategy</label>
                <select className="w-full border rounded-lg px-4 py-2">
                  <option>Cost-plus pricing</option>
                  <option>Market-based pricing</option>
                  <option>Value-based pricing</option>
                  <option>Competitive pricing</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Distribution Channel</label>
                <select className="w-full border rounded-lg px-4 py-2">
                  <option>Direct to customer</option>
                  <option>Through retailers</option>
                  <option>Online</option>
                  <option>Wholesale/Distribution</option>
                </select>
              </div>
            </div>
          </div>
        );
      
      case 6:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Financial Projections</h2>
            <p className="text-gray-600">Project your revenue, costs, and profitability</p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                💰 This section will open the detailed Financial Projections builder with monthly inputs.
              </p>
            </div>
            
            <button 
              onClick={() => window.open('/bank/financials/demo', '_blank')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
            >
              Open Financial Projections →
            </button>
            
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white border rounded-lg p-4">
                <div className="text-sm text-gray-600">Year 1 Revenue Target</div>
                <input type="number" placeholder="e.g., 10000000" className="w-full border rounded px-3 py-2 mt-2" />
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-sm text-gray-600">Gross Margin %</div>
                <input type="number" placeholder="e.g., 40" className="w-full border rounded px-3 py-2 mt-2" />
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-sm text-gray-600">Operating Margin %</div>
                <input type="number" placeholder="e.g., 15" className="w-full border rounded px-3 py-2 mt-2" />
              </div>
            </div>
          </div>
        );
      
      case 7:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Funding Request</h2>
            <p className="text-gray-600">Specify how much funding you need and how you'll use it</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Amount Requested *</label>
                <input
                  type="number"
                  placeholder="e.g., 2000000"
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Loan Term (months)</label>
                <input
                  type="number"
                  placeholder="e.g., 36"
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Use of Funds</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input type="number" placeholder="Amount" className="w-1/3 border rounded px-3 py-2" />
                  <input type="text" placeholder="Purpose (e.g., Equipment)" className="flex-1 border rounded px-3 py-2" />
                </div>
                <button className="text-blue-600 text-sm">+ Add Another</button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Collateral Offered</label>
              <textarea
                placeholder="Describe assets you can offer as collateral..."
                className="w-full border rounded-lg px-4 py-2 h-24"
              />
            </div>
          </div>
        );
      
      case 8:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Risk Assessment</h2>
            <p className="text-gray-600">Identify potential risks and mitigation strategies</p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ This section will open the Risk Register to add and manage risks.
              </p>
            </div>
            
            <button 
              onClick={() => window.open('/bank/risk-register/demo', '_blank')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
            >
              Open Risk Register →
            </button>
            
            <div className="text-sm text-gray-600">
              <p className="mb-2">Minimum 3 risks recommended:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Market risks (competition, demand changes)</li>
                <li>Financial risks (cash flow, credit)</li>
                <li>Operational risks (supply chain, production)</li>
              </ul>
            </div>
          </div>
        );
      
      case 9:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Review & Submit</h2>
            <p className="text-gray-600">Review your plan completeness before submitting to the bank</p>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Plan Completeness</span>
                  <span className="text-2xl font-bold text-blue-600">{completeness}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full ${completeness >= 90 ? 'bg-green-500' : 'bg-yellow-500'}`}
                    style={{width: `${completeness}%`}}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {completeness >= 90 ? '✅ Ready to submit!' : '⚠️ Must be 90% complete to submit'}
                </p>
              </div>
              
              <div className="space-y-2 mb-6">
                <h3 className="font-bold">Completed Sections:</h3>
                {steps.slice(0, -1).map(step => (
                  <div key={step.id} className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>{step.icon} {step.title}</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={savePlan}
                disabled={completeness < 90}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                Submit Plan for Bank Review
              </button>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">Section {currentStep}</h2>
            <p className="text-gray-600">Content for this section will be added in next phase</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold">Create Business Plan</h1>
            <span className="text-sm text-gray-600">Step {currentStep} of {steps.length}</span>
          </div>
          <div className="flex gap-2">
            {steps.map(step => (
              <div
                key={step.id}
                className={`flex-1 h-2 rounded-full ${
                  step.id < currentStep ? 'bg-green-500' :
                  step.id === currentStep ? 'bg-blue-500' :
                  'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {renderStepContent()}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-8 border-t">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            <div className="flex gap-2">
              <button
                onClick={savePlan}
                className="px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                💾 Save Draft
              </button>
              <button
                onClick={nextStep}
                disabled={currentStep === steps.length}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedPlanBuilder;